import React, { useState, useEffect } from 'react';
import { Modal } from './ui/Modal';
import { Download, Loader, AlertCircle, Globe2 } from 'lucide-react';
import publicApiService from '../services/publicApiService';
import { workspaceService } from '../services/workspaceService';
import {
  buildWorkspacePublishPayload,
  collectionVariablesFromPackage,
} from '../services/workspacePublishPayload';

/**
 * Payload for `POST /workspaces/publish-local` from an API Network package.
 *
 * Same endpoint, same private same-account destination as
 * `buildPublishLocalPayload` — this used to be a hand-copied duplicate of that
 * function's shape, which is how one of two copies gets a fix and the other
 * does not. The shape now lives once, in `services/workspacePublishPayload.js`.
 *
 * Only the *source* shape differs and stays here: an import package nests the
 * variable scopes under `workspace.settings`, falls back to the listing card's
 * name/summary, and carries a collection's variables at the top level rather
 * than under `variable_scopes` (`PublicApis.sanitize_collection/1` emits no
 * `variable_scopes` at all) — hence `collectionVariablesFromPackage`.
 *
 * The package arriving here is already redacted server-side by
 * `PublicApis.Redactor`; nothing on this path redacts again, and nothing on this
 * path may set `visibility` — see the moduledoc in `workspacePublishPayload.js`.
 *
 * Exported for `src/tests/publishLocalPayload.test.js`, which asserts this call
 * path's payload is unchanged by the merge.
 */
export function buildImportPayload(pkg, listingMeta = {}) {
  const ws = pkg.workspace || {};

  return buildWorkspacePublishPayload({
    name: ws.name || listingMeta.name || 'Imported API',
    description: ws.description || listingMeta.summary || '',
    variableScopes: {
      globals: ws.settings?.global_variables,
      workspace_globals: ws.settings?.workspace_variables,
      environments: ws.settings?.environments,
    },
    collections: pkg.collections,
    collectionVariablesOf: collectionVariablesFromPackage,
  });
}

const ImportNetworkModal = ({
  isOpen,
  onClose,
  slug,
  showToast,
  onImported,
}) => {
  const [pkg, setPkg] = useState(null);
  const [meta, setMeta] = useState(null);
  const [fetchError, setFetchError] = useState('');
  const [fetching, setFetching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(false);

  useEffect(() => {
    if (!isOpen || !slug) return;

    setPkg(null);
    setMeta(null);
    setFetchError('');
    setImported(false);
    setFetching(true);

    publicApiService
      .fetchImportPackage(slug)
      .then((data) => {
        setPkg(data);
        setMeta(data.listing || data.meta || null);
      })
      .catch((err) => setFetchError(err.message))
      .finally(() => setFetching(false));
  }, [isOpen, slug]);

  const handleClose = () => {
    setPkg(null);
    setMeta(null);
    setFetchError('');
    setImported(false);
    onClose();
  };

  const handleImport = async () => {
    if (!slug || !pkg || importing) return;
    setImporting(true);
    try {
      const payload = buildImportPayload(pkg, meta || {});
      const result = await workspaceService.publishLocal(payload);
      // publishLocal's response shape isn't guaranteed; accept either a nested
      // { workspace } or a top-level workspace object, and never crash on name.
      const cloudWs = result?.workspace || result;
      setImported(true);
      showToast?.(`"${cloudWs?.name || pkg?.workspace?.name || 'Workspace'}" imported to your account`, 'success');
      onImported?.(cloudWs, result?.collections || []);
      // Best-effort analytics: only count an import that actually succeeded,
      // and never let the counter failing surface as an import error.
      publicApiService.countImport(slug).catch(() => {});
      setTimeout(() => handleClose(), 1200);
    } catch (err) {
      showToast?.(err.message, 'error');
    } finally {
      setImporting(false);
    }
  };

  const wsName = pkg?.workspace?.name || meta?.name || slug;
  const publisher =
    meta?.team?.name || meta?.publisher_name || pkg?.publisher_name || 'Unknown publisher';
  const requestsCount =
    meta?.requests_count ??
    (pkg?.collections || []).reduce((n, c) => n + (c.requests?.length || 0), 0);
  const collectionsCount =
    meta?.collections_count ?? (pkg?.collections || []).length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import from API Network"
      customIcon={<Globe2 size={22} className="text-violet-500" />}
    >
      <div className="space-y-4">
        {fetching && (
          <div className="flex items-center justify-center py-10">
            <Loader size={22} className="animate-spin text-violet-500" />
          </div>
        )}

        {fetchError && !fetching && (
          <div className="flex items-start gap-3 rounded-lg border border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 p-3">
            <AlertCircle size={15} className="mt-0.5 flex-shrink-0 text-red-500" />
            <p className="text-sm text-red-600 dark:text-red-400">{fetchError}</p>
          </div>
        )}

        {pkg && !fetching && (
          <>
            <div className="flex flex-col items-center text-center pb-1">
              <p className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{wsName}</p>
              <p className="mt-1 text-xs text-gray-400 dark:text-zinc-500">
                Published by{' '}
                <span className="text-gray-600 dark:text-zinc-400 font-medium">{publisher}</span>
              </p>
              <p className="mt-1 text-xs text-gray-400 dark:text-zinc-500">
                {collectionsCount} collection{collectionsCount === 1 ? '' : 's'} ·{' '}
                {requestsCount} request{requestsCount === 1 ? '' : 's'}
              </p>
            </div>

            <div className="border-t border-gray-100 dark:border-zinc-700/60" />

            {imported ? (
              <div className="rounded-lg border border-green-200 dark:border-green-800/40 bg-green-50 dark:bg-green-900/20 p-3 text-center text-sm font-medium text-green-700 dark:text-green-400">
                ✓ Workspace imported successfully!
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600 dark:text-zinc-400">
                  Creates a personal cloud workspace with a snapshot of this API. You can edit your
                  copy independently — changes to the published source won&apos;t sync automatically.
                </p>
                <button
                  type="button"
                  onClick={handleImport}
                  disabled={importing}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {importing ? (
                    <>
                      <Loader size={14} className="animate-spin" />
                      <span>Importing…</span>
                    </>
                  ) : (
                    <>
                      <Download size={14} />
                      <span>Import as personal workspace</span>
                    </>
                  )}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default ImportNetworkModal;
